import { useEffect, useState, useCallback, useRef } from "react";
import { playerApi, wsUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Loader2, X, Trophy, Timer, Check, Coins } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";

const fireConfetti = () => {
  confetti({ particleCount: 140, spread: 80, origin: { y: 0.6 }, colors: ["#FF3B30", "#FFCC00", "#34C759", "#007AFF"] });
};

export default function GamePlayer({ slug, zone, zoneId, onClose, onComplete }) {
  if (zone?.control_mode === "hosted") {
    return <LiveGamePlayer slug={slug} zoneId={zoneId || zone?.zone_id} zoneName={zone?.name} onClose={onClose} onComplete={onComplete} />;
  }
  return <AutoGamePlayer slug={slug} zoneId={zoneId || zone?.zone_id} onClose={onClose} onComplete={onComplete} />;
}

function AutoGamePlayer({ slug, zoneId, onClose, onComplete }) {
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading, memorize, playing, result, error
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [matches, setMatches] = useState({});
  const [selLeft, setSelLeft] = useState(null);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [startTime] = useState(Date.now());
  const [errMsg, setErrMsg] = useState("");
  const [chosen, setChosen] = useState(null);

  const api = playerApi(slug);

  useEffect(() => {
    api.get(`/public/events/${slug}/zones/${zoneId}/play`)
      .then((r) => {
        setData(r.data);
        if (r.data.zone.game_type === "memory_challenge") setPhase("memorize");
        else setPhase("playing");
      })
      .catch((e) => { setErrMsg(e.response?.data?.detail || "Cannot start game"); setPhase("error"); });
    // eslint-disable-next-line
  }, [slug, zoneId]);

  const gtype = data?.zone?.game_type;
  const questions = data?.questions || [];
  const q = questions[idx];

  // per-question timer for rapid_fire / mystery_vault
  const submit = useCallback(async (finalAnswers, finalMatches) => {
    setPhase("submitting");
    try {
      const r = await api.post(`/public/events/${slug}/zones/${zoneId}/submit`, {
        answers: finalAnswers, matches: finalMatches, time_taken: Math.round((Date.now() - startTime) / 1000),
      });
      setResult(r.data);
      setPhase("result");
      if (r.data.xp_earned > 0) fireConfetti();
    } catch (e) {
      setErrMsg(e.response?.data?.detail || "Submit failed"); setPhase("error");
    }
  }, [api, slug, zoneId, startTime]);

  useEffect(() => {
    if (phase === "memorize") {
      const t = setTimeout(() => setPhase("playing"), 6000);
      return () => clearTimeout(t);
    }
  }, [phase]);

  useEffect(() => {
    if (phase !== "playing" || !q || gtype === "match_master") return;
    if (!["rapid_fire", "mystery_vault"].includes(gtype)) return;
    setTimeLeft(q.time_limit || 20);
    const iv = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(iv); goNext(null); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [idx, phase, gtype]);

  const goNext = (selected) => {
    const na = selected == null ? answers : { ...answers, [q.question_id]: selected };
    if (selected != null) setAnswers(na);
    setChosen(null);
    if (idx + 1 >= questions.length) { submit(na, {}); }
    else setIdx(idx + 1);
  };

  const pick = (i) => {
    if (chosen != null) return;
    setChosen(i);
    setTimeout(() => goNext(i), 450);
  };

  // ---------- match master ----------
  const tapLeft = (l) => setSelLeft(l);
  const tapRight = (r) => {
    if (!selLeft) { toast.info("Pick an item on the left first"); return; }
    const nm = { ...matches, [selLeft]: r };
    setMatches(nm);
    setSelLeft(null);
    if (Object.keys(nm).length >= (data.config.lefts?.length || 0)) submit({}, nm);
  };

  const close = () => { onClose?.(); };

  const optionStyle = (i) => {
    const base = "w-full text-left p-4 rounded-2xl border-2 border-neutral-300 font-semibold transition-transform lift ";
    if (chosen === i) return base + "bg-primary text-white scale-[1.02]";
    if (gtype === "pixel_quest") return base + "bg-neon-green/15 hover:bg-neon-green/25 text-foreground";
    if (gtype === "target_master") return base + "bg-neon-pink/15 hover:bg-neon-pink/25 rounded-full text-center";
    if (gtype === "mystery_vault") return base + "bg-[#5856D6]/20 hover:bg-[#5856D6]/30 font-mono";
    return base + "bg-card hover:bg-secondary";
  };

  return (
    <Dialog open onOpenChange={close}>
      <DialogContent className="max-w-lg p-0 border-2 border-neutral-300 bg-background overflow-hidden" data-testid="game-player">
        <DialogTitle className="sr-only">{data?.zone?.name || "Challenge"}</DialogTitle>
        <div className="p-5 min-h-[420px] flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="font-head font-black text-lg truncate">{data?.zone?.name || "Loading"}</div>
            <button onClick={close} data-testid="game-close"><X className="w-5 h-5 text-muted-foreground" /></button>
          </div>

          {phase === "loading" && <div className="flex-1 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}

          {phase === "error" && (
            <div className="flex-1 grid place-items-center text-center gap-4">
              <p className="text-muted-foreground">{errMsg}</p>
              <Button onClick={close} className="rounded-full">Close</Button>
            </div>
          )}

          {phase === "memorize" && (
            <div className="flex-1 flex flex-col items-center justify-center gap-4">
              <p className="font-head font-bold text-xl">Memorise these! 🧠</p>
              <div className="grid grid-cols-3 gap-3">
                {(data.config.cards || []).map((c, i) => (
                  <div key={i} className="brut-hard bg-card p-4 rounded-xl text-center font-semibold animate-pop" style={{ animationDelay: `${i * 60}ms` }}>{c.replace("-decoy", "")}</div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground font-mono-num">Hiding in a few seconds…</p>
            </div>
          )}

          {phase === "playing" && gtype === "match_master" && (
            <div className="flex-1">
              <p className="text-sm text-muted-foreground mb-3">Tap an item on the left, then its match on the right.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  {data.config.lefts?.map((l) => (
                    <button key={l} onClick={() => tapLeft(l)} disabled={matches[l]}
                      className={`w-full p-3 rounded-xl border-2 border-neutral-300 font-semibold ${matches[l] ? "opacity-40" : selLeft === l ? "bg-primary text-white" : "bg-card"}`}
                      data-testid={`match-left-${l}`}>{l}{matches[l] && <Check className="w-4 h-4 inline ml-1" />}</button>
                  ))}
                </div>
                <div className="space-y-2">
                  {data.config.rights?.map((r) => (
                    <button key={r} onClick={() => tapRight(r)} disabled={Object.values(matches).includes(r)}
                      className={`w-full p-3 rounded-xl border-2 border-neutral-300 font-semibold ${Object.values(matches).includes(r) ? "opacity-40" : "bg-secondary hover:bg-card"}`}
                      data-testid={`match-right-${r}`}>{r}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {phase === "playing" && gtype !== "match_master" && q && (
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono-num text-muted-foreground">Q{idx + 1}/{questions.length}</span>
                {["rapid_fire", "mystery_vault"].includes(gtype) && (
                  <span className="flex items-center gap-1 text-sm font-mono-num text-gold"><Timer className="w-4 h-4" />{timeLeft}s</span>
                )}
              </div>
              <h3 className="font-head font-bold text-xl mb-5">{q.text}</h3>
              <div className="space-y-3">
                {q.options.map((opt, i) => (
                  <button key={i} className={optionStyle(i)} onClick={() => pick(i)} data-testid={`game-option-${i}`}>
                    <span className="font-mono-num mr-2 opacity-60">{String.fromCharCode(65 + i)}</span>{opt}
                  </button>
                ))}
              </div>
            </div>
          )}

          {phase === "submitting" && <div className="flex-1 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}

          {phase === "result" && result && (
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 animate-pop" data-testid="game-result">
              <Trophy className="w-16 h-16 text-gold animate-floaty" />
              <h2 className="font-head font-black text-3xl">{result.perfect ? "PERFECT!" : result.xp_earned > 0 ? "NICE!" : "GAME OVER"}</h2>
              {result.total > 0 && <p className="text-muted-foreground">You got {result.correct}/{result.total} correct</p>}
              <div className="flex items-center gap-2 font-mono-num font-extrabold text-5xl text-cash"><Coins className="w-9 h-9" />+{result.xp_earned}</div>
              <p className="text-sm text-muted-foreground">Total: <span className="font-mono-num text-cash">{result.total_xp.toLocaleString()} Cash</span></p>
              <Button className="rounded-full px-8 mt-2" onClick={() => { onComplete?.(); close(); }} data-testid="game-done">Awesome!</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function LiveGamePlayer({ slug, zoneId, zoneName, onClose, onComplete }) {
  const [snap, setSnap] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [answeredQ, setAnsweredQ] = useState(null);
  const api = playerApi(slug);

  useEffect(() => {
    let alive = true;
    let ws;
    const connect = () => {
      ws = new WebSocket(wsUrl(`/api/ws/events/${slug}/zones/${zoneId}`));
      ws.onmessage = (e) => {
        const d = JSON.parse(e.data);
        if (d.type === "state") setSnap(d);
        else if (d.type === "tally") setSnap((s) => (s ? { ...s, answered_count: d.answered_count } : s));
      };
      ws.onclose = () => { if (alive) setTimeout(connect, 1500); };
    };
    connect();
    return () => { alive = false; try { ws && ws.close(); } catch (e) {} };
  }, [slug, zoneId]);

  const qid = snap?.question?.question_id;
  useEffect(() => { setChosen(null); }, [qid]);

  const state = snap?.state;
  const q = snap?.question;
  const revealed = state === "revealed";
  const correct = snap?.correct_index;

  const pick = async (i) => {
    if (chosen != null || state !== "running") return;
    setChosen(i); setAnsweredQ(qid);
    try { await api.post(`/public/events/${slug}/zones/${zoneId}/answer`, { question_id: qid, option: i }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); setChosen(null); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg p-0 border-2 border-neutral-300 bg-background overflow-hidden" data-testid="live-game-player">
        <DialogTitle className="sr-only">{zoneName}</DialogTitle>
        <div className="p-5 min-h-[420px] flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="font-head font-black text-lg truncate flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-primary animate-pulse" />LIVE · {zoneName}</div>
            <button onClick={onClose} data-testid="live-close"><X className="w-5 h-5 text-muted-foreground" /></button>
          </div>

          {!snap && <div className="flex-1 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}

          {snap && (state === "idle" || state === "paused") && (
            <div className="flex-1 grid place-items-center text-center gap-3" data-testid="live-waiting">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="font-head font-bold text-lg">Waiting for the host to start…</p>
              <p className="text-sm text-muted-foreground">The question appears here the moment the moderator begins.</p>
            </div>
          )}

          {snap && (state === "running" || revealed) && q && (
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-mono-num text-muted-foreground">Q{(snap.current_question_index ?? 0) + 1}/{snap.total_questions}</span>
                <span className="text-xs font-mono-num text-muted-foreground">{snap.answered_count || 0} answered</span>
              </div>
              <h3 className="font-head font-bold text-xl mb-5">{q.text}</h3>
              <div className="space-y-3">
                {q.options.map((opt, i) => {
                  let cls = "w-full text-left p-4 rounded-2xl border-2 border-neutral-300 font-semibold transition-transform ";
                  if (revealed) cls += i === correct ? "bg-neon-green/20 border-neon-green" : (chosen === i ? "bg-primary/20 border-primary" : "opacity-60");
                  else cls += chosen === i ? "bg-primary text-white" : (chosen != null ? "opacity-60" : "bg-card hover:bg-secondary lift");
                  return <button key={i} className={cls} disabled={chosen != null || revealed} onClick={() => pick(i)} data-testid={`live-option-${i}`}><span className="font-mono-num mr-2 opacity-60">{String.fromCharCode(65 + i)}</span>{opt}</button>;
                })}
              </div>
              {chosen != null && !revealed && <p className="text-center text-sm text-muted-foreground mt-4">Answer locked — waiting for the host…</p>}
              {revealed && answeredQ === qid && <p className="text-center font-head font-bold mt-4">{chosen === correct ? `Correct! +${q.xp} Cash 🎉` : "Not this time 😅"}</p>}
            </div>
          )}

          {snap && state === "ended" && (
            <div className="flex-1 grid place-items-center text-center gap-4" data-testid="live-ended">
              <Trophy className="w-16 h-16 text-gold animate-floaty" />
              <h2 className="font-head font-black text-3xl">Quiz complete!</h2>
              <p className="text-sm text-muted-foreground">Check the Hall of Fame to see where you landed.</p>
              <Button className="rounded-full px-8" onClick={() => { onComplete?.(); onClose?.(); }} data-testid="live-done">Done</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
